import { z } from "zod";
import { reconstructedCheckoutRowSchema } from "./reconstructedCheckouts";

import {
  appointmentRefundPaymentSourceSchema,
  appointmentRefundStatusSchema,
} from "./appointments";

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

/**
 * Status of the platform → staff transfer for a given AppointmentCheckout.
 *
 * - `transferred`: a staff transfer succeeded (`staffTransferId` is set).
 * - `skipped_zero`: the computed staff share was 0.
 * - `skipped_no_account`: the assigned member has no Stripe account connected.
 * - `failed`: the transfer attempt failed (`splitFailureReason` is set).
 * - `n/a`: payment was cash/manual — no Stripe activity, no split applies.
 */
export const splitStatusSchema = z.enum([
  "transferred",
  "skipped_zero",
  "skipped_no_account",
  "failed",
  "n/a",
]);
export type SplitStatus = z.infer<typeof splitStatusSchema>;

/**
 * Range query param used by transactions / earnings endpoints.
 *
 * Either pass `range` (preset window ending now) OR `start`+`end` (custom
 * inclusive YYYY-MM-DD window). When both are provided, `start`/`end` win.
 * Custom ranges may include an IANA `timezone`; otherwise dates are UTC days.
 */
export const rangeQuerySchema = z
  .object({
    range: z.enum(["30d", "90d"]).default("30d"),
    start: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
      .optional(),
    end: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
      .optional(),
    timezone: z.string().optional(),
  })
  .refine(
    (v) => (v.start == null) === (v.end == null),
    "Both `start` and `end` are required when using a custom range",
  );
export type RangeQuery = z.infer<typeof rangeQuerySchema>;

/**
 * Inclusive YYYY-MM-DD date-range query used by staff earnings endpoints.
 * Both start and end are required. They represent calendar days in the
 * caller's `timezone` when supplied, otherwise UTC days.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
export const dateRangeQuerySchema = z.object({
  start: isoDate,
  end: isoDate,
  timezone: z.string().optional(),
});
export type DateRangeQuery = z.infer<typeof dateRangeQuerySchema>;

// ---------------------------------------------------------------------------
// Stripe payouts (balance + payout list)
// ---------------------------------------------------------------------------

export const payoutsBalanceSchema = z.object({
  /** Cents available for next payout. */
  available: z.number().int(),
  /** Cents pending settlement. */
  pending: z.number().int(),
  currency: z.string(),
});
export type PayoutsBalance = z.infer<typeof payoutsBalanceSchema>;

export const payoutSchema = z.object({
  id: z.string(),
  /** Cents. */
  amount: z.number().int(),
  currency: z.string(),
  /** Stripe payout status (`paid`, `pending`, `in_transit`, `failed`, …). */
  status: z.string(),
  /** ISO date. */
  arrivalDate: z.string().nullable(),
  /** `standard` | `instant` | other. */
  method: z.string().nullable(),
  /** ISO date. */
  createdAt: z.string(),
});
export type Payout = z.infer<typeof payoutSchema>;

/**
 * Forward-looking estimate of the next scheduled payout. `date` is the ISO
 * datetime of the next cycle run at the business's local payout hour (see
 * `payoutSchedule.ts`); `amountMinor` is the ledger balance whose
 * `payoutEligibleAt` is on/before that datetime — so Stripe's business-day
 * settlement window is already baked into the figure. `null` when there is no
 * connected payout account or nothing is eligible by then.
 */
export const estimatedPayoutSchema = z.object({
  /** ISO datetime of the next scheduled payout run. */
  date: z.string(),
  /** Cents eligible for payout by `date`. */
  amountMinor: z.number().int(),
  currency: z.string(),
});
export type EstimatedPayout = z.infer<typeof estimatedPayoutSchema>;

export const payoutsSummarySchema = z.object({
  balance: payoutsBalanceSchema,
  nextPayout: payoutSchema.nullable(),
  lastPayout: payoutSchema.nullable(),
  currency: z.string(),
  /** `false` when the underlying Stripe account is not connected. */
  connected: z.boolean(),
  /**
   * Net amount the platform owes this business from the internal ledger,
   * awaiting a future payout cycle. Distinct from the Stripe `balance` above:
   * under the platform-ledger model charges land on the platform account, so
   * the connected account's Stripe balance reads $0 until the payout worker
   * transfers funds. `available` = payout-eligible now (next cycle);
   * `pending` = earned but still in Stripe's settlement window. `null` when
   * the ledger has no rows for this business.
   */
  ledgerBalance: payoutsBalanceSchema.nullable(),
  /** Estimated next scheduled payout (date + eligible amount). */
  nextEstimatedPayout: estimatedPayoutSchema.nullable(),
});
export type PayoutsSummary = z.infer<typeof payoutsSummarySchema>;

/**
 * Who settles the owner's appointment card payments (Sep 2026). Read-only —
 * the emergency switch `APPOINTMENT_ON_BEHALF_OF_DISABLED_SURFACES` is operator-controlled. Evaluated with
 * the same rules as real charges (`payments/appointmentSettlement.ts`).
 */
export const appointmentChargeSurfaceSchema = z.enum([
  "online",
  "pos_terminal",
  "tap_to_pay",
]);
export type AppointmentChargeSurface = z.infer<
  typeof appointmentChargeSurfaceSchema
>;

export const appointmentSettlementReasonSchema = z.enum([
  "connected_business",
  "surface_disabled",
  "no_business_account",
  "card_payments_inactive",
  "account_lookup_failed",
]);

export const appointmentSettlementStatusSchema = z.object({
  account: z.object({
    /** The owner has a Stripe account id on file. */
    connected: z.boolean(),
    /** `card_payments` active and `charges_enabled`. */
    cardPaymentsActive: z.boolean(),
    mcc: z.string().nullable(),
    statementDescriptor: z.string().nullable(),
    /** The category supports tips after card approval (MCC 7230 / 7298). */
    tipAfterApprovalCategory: z.boolean(),
  }),
  surfaces: z.array(
    z.object({
      surface: appointmentChargeSurfaceSchema,
      /** Turned on by Openings for this channel. */
      enabled: z.boolean(),
      /** Charges on this channel settle as the business. */
      settlesAsBusiness: z.boolean(),
      reason: appointmentSettlementReasonSchema,
    }),
  ),
});
export type AppointmentSettlementStatus = z.infer<
  typeof appointmentSettlementStatusSchema
>;

export const payoutsListSchema = z.object({
  payouts: z.array(payoutSchema),
  hasMore: z.boolean(),
});
export type PayoutsList = z.infer<typeof payoutsListSchema>;

// ---------------------------------------------------------------------------
// Business transactions (checkout rows and charged booking deposits)
// ---------------------------------------------------------------------------

export const transactionKindSchema = z.enum(["CHECKOUT", "DEPOSIT", "SALE"]);
export type TransactionKind = z.infer<typeof transactionKindSchema>;

/**
 * Aggregated refund state of a transaction row:
 * - `none`: no non-declined refunds recorded.
 * - `pending`: refunds exist but all are still pending with Stripe.
 * - `partial`: some amount refunded, less than the original gross.
 * - `refunded`: fully refunded (refunded total >= original gross).
 */
export const transactionRefundStatusSchema = z.enum([
  "none",
  "pending",
  "partial",
  "refunded",
]);
export type TransactionRefundStatus = z.infer<
  typeof transactionRefundStatusSchema
>;

export const appointmentAdjustmentKindSchema = z.enum(["REFUND", "DISPUTE"]);
export type AppointmentAdjustmentKind = z.infer<
  typeof appointmentAdjustmentKindSchema
>;

export type AppointmentAdjustmentPaymentSource = z.infer<
  typeof appointmentRefundPaymentSourceSchema
>;

export const appointmentAdjustmentEventStatusSchema = z.enum([
  "PENDING",
  "SUCCEEDED",
  "FAILED",
  "CANCELED",
  "REVERSED",
]);
export type AppointmentAdjustmentEventStatus = z.infer<
  typeof appointmentAdjustmentEventStatusSchema
>;

export const appointmentAdjustmentAllocationMethodSchema = z.enum([
  "AUTOMATIC",
  "ADMIN",
  "UNALLOCATED",
]);
export type AppointmentAdjustmentAllocationMethod = z.infer<
  typeof appointmentAdjustmentAllocationMethodSchema
>;

/**
 * Canonical payment-adjustment fields shared by refund-history and
 * transaction refund records. `status` is the history presentation projection
 * (including compensating reinstatements); `eventStatus` is authoritative.
 */
export const adjustmentHistoryStatusSchema = z.union([
  appointmentRefundStatusSchema,
  z.literal("REINSTATED"),
]);
export type AdjustmentHistoryStatus = z.infer<
  typeof adjustmentHistoryStatusSchema
>;

const canonicalAdjustmentReadSchema = z.object({
  status: adjustmentHistoryStatusSchema,
  /** Added in the canonical-adjustment rollout; absent on older API payloads. */
  paymentSource: appointmentRefundPaymentSourceSchema.optional(),
  /** Added in the canonical-adjustment rollout; absent means legacy refund. */
  adjustmentKind: appointmentAdjustmentKindSchema.optional(),
  /** Authoritative when present; legacy consumers can still use `status`. */
  eventStatus: appointmentAdjustmentEventStatusSchema.optional(),
  /** Cents attributed to service revenue. */
  serviceAmountMinor: z.number().int().nonnegative().optional(),
  /** Cents attributed to gratuity. */
  tipAmountMinor: z.number().int().nonnegative().optional(),
  /** Cents attributed to a booking deposit. */
  depositAmountMinor: z.number().int().nonnegative().optional(),
  /** Cents awaiting an explicit service/tip/deposit allocation. */
  unallocatedAmountMinor: z.number().int().nonnegative().optional(),
  allocationMethod: appointmentAdjustmentAllocationMethodSchema.optional(),
  /**
   * Original adjustment compensated by this row. Non-null rows remain in
   * history for audit but do not contribute to refunded transaction totals.
   */
  reversalOfId: z.string().nullable().optional().default(null),
});

/** One `AppointmentRefund` row surfaced on a transaction. */
export const transactionRefundSchema = canonicalAdjustmentReadSchema.extend({
  id: z.string(),
  /** Cents. */
  amountMinor: z.number().int(),
  reason: z.string(),
  /** `DASHBOARD` | `STAFF_PORTAL` | `CANCELLATION` | `DISPUTE` | `WEBHOOK`. */
  origin: z.string(),
  /** Display name of whoever issued the refund (owner or staff), when known. */
  actorName: z.string().nullable(),
  /** ISO timestamp. */
  createdAt: z.string(),
});
export type TransactionRefund = z.infer<typeof transactionRefundSchema>;

/** One payment rail in the appointment-level transaction reconciliation. */
export const transactionPaymentPartSchema = z.object({
  /** Cents. Service amount on this rail, excluding tip. */
  price: z.number().int(),
  /** Cents. Tip captured on this rail. */
  tip: z.number().int(),
  /** Cents. `price + tip`. */
  gross: z.number().int(),
  /** Cents. Staff ownership attributed to this rail, before fees. */
  staffShare: z.number().int(),
  /** Cents. Business ownership attributed to this rail, before fees. */
  businessShare: z.number().int(),
});
export type TransactionPaymentPart = z.infer<
  typeof transactionPaymentPartSchema
>;

/**
 * Appointment-level reconciliation carried by each source-specific
 * transaction row. Deposit and checkout remain distinct for fees, refunds,
 * dates, and Stripe ids; this summary lets detail surfaces explain how those
 * rails add back up to the customer's full payment and combined split.
 */
export const appointmentPaymentSummarySchema = z.object({
  deposit: transactionPaymentPartSchema.nullable(),
  checkout: transactionPaymentPartSchema.nullable(),
  total: transactionPaymentPartSchema,
});
export type AppointmentPaymentSummary = z.infer<
  typeof appointmentPaymentSummarySchema
>;

/**
 * Component split of a SALE row (Task 13C). A mixed receipt appears ONCE in
 * transaction activity with these components — never as two apparent charges.
 * Cents; `serviceMinor` is the post-discount service total.
 */
export const saleTransactionComponentsSchema = z.object({
  serviceMinor: z.number().int(),
  tipMinor: z.number().int(),
  productNetMinor: z.number().int(),
  taxMinor: z.number().int(),
  depositAppliedMinor: z.number().int(),
});
export type SaleTransactionComponents = z.infer<
  typeof saleTransactionComponentsSchema
>;

/**
 * The offer code an appointment checkout used
 * (plans/business-covered-appointment-offers-spec.md §4). Cents: `grossMinor`
 * is the balance before the coupon, `discountMinor` what the coupon took off.
 */
export const checkoutOfferSummarySchema = z.object({
  offerName: z.string(),
  code: z.string(),
  costBearer: z.enum(["BUSINESS", "SHARED"]),
  grossMinor: z.number().int(),
  discountMinor: z.number().int(),
  serviceDiscountMinor: z.number().int().optional(),
  productDiscountMinor: z.number().int().optional(),
});
export type CheckoutOfferSummary = z.infer<typeof checkoutOfferSummarySchema>;

export const transactionRowSchema = z.object({
  /**
   * Stable row id, e.g. `checkout:<checkoutId>`, `deposit:<appointmentId>`,
   * or `sale:<saleId>`.
   */
  id: z.string(),
  kind: transactionKindSchema,
  /** Null for standalone SALE rows (no appointment involved). */
  appointmentId: z.string().nullable(),
  customerName: z.string().nullable(),
  serviceLabel: z.string(),
  staffName: z.string().nullable(),
  /** ISO date — `AppointmentCheckout.createdAt`. */
  paidAt: z.string(),
  /** Cents. service price (excludes tip). */
  price: z.number().int(),
  /** Cents. tip portion (0 when none). */
  tip: z.number().int(),
  /** Source of payment, e.g. "CASH", "TERMINAL", "ONLINE". */
  paymentType: z.string(),
  /** Checkout capture channel, e.g. "TAP_TO_PAY", "MANUAL". Null for deposits. */
  captureType: z.string().nullable(),
  /** Cents. price + tip. */
  gross: z.number().int(),
  /** Cents. From Stripe `balance_transaction.fee` when enriched, else null. */
  fee: z.number().int().nullable(),
  /**
   * Cents. Portion of the Stripe fee absorbed by the business (per the
   * `serviceSplitFeePayer` / `tipSplitFeePayer` rules on the team member).
   * Positive number. `null` when no ledger STRIPE_FEE rows have been written
   * yet for this checkout (e.g. fee enrichment hasn't fired).
   */
  feeBusinessAbsorbed: z.number().int().nullable(),
  /**
   * Cents. Portion of the Stripe fee absorbed by the staff member. Positive
   * number. `null` when fee allocation hasn't been recorded yet.
   */
  feeStaffAbsorbed: z.number().int().nullable(),
  /**
   * Cents. Openings platform fee attributed to this payment source. Sourced
   * from the balanced canonical `PLATFORM_FEE` ledger pair and its versioned
   * deposit/checkout provenance. `AppointmentCheckout.applicationFeeMinor` is
   * legacy Stripe Connect routing metadata and is not authoritative here.
   */
  platformFee: z.number().int().nullable(),
  /** Cents. */
  staffShare: z.number().int(),
  /** Cents. */
  businessShare: z.number().int(),
  /**
   * Combined appointment payment/split reconciliation. Optional so a newer
   * dashboard remains compatible with an older API during rolling deploys.
   * Source-level fields above stay authoritative for refunds and charge fees.
   */
  appointmentPayments: appointmentPaymentSummarySchema.optional(),
  splitStatus: splitStatusSchema,
  /**
   * Compatibility routing case captured by the checkout path. Under the
   * platform-ledger model this metadata does not prove that Stripe routed or
   * transferred funds at charge time; use ledger/payout state for that.
   */
  splitRouting: z.string().nullable(),
  splitFailureReason: z.string().nullable(),
  stripeChargeId: z.string().nullable(),
  staffTransferId: z.string().nullable(),
  /**
   * Cents. Total non-declined refunds recorded against this transaction.
   * Defaults keep older API payloads (without refund enrichment) parseable.
   */
  refundedMinor: z.number().int().default(0),
  refundStatus: transactionRefundStatusSchema.default("none"),
  /** Individual refunds, newest first. */
  refunds: z.array(transactionRefundSchema).default([]),
  /** Sale identity for SALE rows; null on checkout/deposit rows. */
  saleId: z.string().nullable().default(null),
  saleNumber: z.string().nullable().default(null),
  /** Component split for SALE rows; null on checkout/deposit rows. */
  saleComponents: saleTransactionComponentsSchema.nullable().default(null),
  /** Offer code a CHECKOUT row used; null on deposit and sale rows. */
  offer: checkoutOfferSummarySchema.nullable().default(null),
});
export type TransactionRow = z.infer<typeof transactionRowSchema>;

export const transactionsListSchema = z.object({
  rows: z.array(transactionRowSchema),
});
export type TransactionsList = z.infer<typeof transactionsListSchema>;

/** Response of the single-transaction lookup endpoint. */
export const transactionGetSchema = z.object({
  row: transactionRowSchema,
});
export type TransactionGet = z.infer<typeof transactionGetSchema>;

// ---------------------------------------------------------------------------
// Refund history (owner + staff lists over `AppointmentRefund`)
// ---------------------------------------------------------------------------

/** One refund row in the refunds history list. */
export const refundListRowSchema = canonicalAdjustmentReadSchema.extend({
  /** `AppointmentRefund.id`, or `PaymentAdjustment.id` for a sale refund. */
  id: z.string(),
  /** ISO timestamp the refund was issued. */
  createdAt: z.string(),
  /** Cents refunded. */
  amountMinor: z.number().int(),
  currency: z.string(),
  reason: z.string(),
  /** `DASHBOARD` | `STAFF_PORTAL` | `CANCELLATION` | `DISPUTE` | `WEBHOOK`. */
  origin: z.string(),
  appointmentId: z.string(),
  /** Transaction row id (`checkout:<id>` / `deposit:<appointmentId>` / `sale:<saleId>`). */
  transactionId: z.string(),
  kind: transactionKindSchema,
  customerName: z.string().nullable(),
  /**
   * The refunded appointment's customer, so the list can open their history.
   * Null for the shared walk-in customer, which has no history of its own.
   * Defaults keep older API payloads parseable.
   */
  customerId: z.string().nullable().default(null),
  serviceLabel: z.string(),
  staffName: z.string().nullable(),
  /** Display name of whoever issued the refund, when known. */
  actorName: z.string().nullable(),
  /** Cents. Original payment (checkout gross or deposit amount). Null when unknown. */
  originalMinor: z.number().int().nullable(),
});
export type RefundListRow = z.infer<typeof refundListRowSchema>;

export const refundsListSchema = z.object({
  rows: z.array(refundListRowSchema),
  /** Total refunds matching the filter (for pagination). */
  total: z.number().int(),
});
export type RefundsList = z.infer<typeof refundsListSchema>;

export const refundsListQuerySchema = z.object({
  start: isoDate.optional(),
  end: isoDate.optional(),
  timezone: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type RefundsListQuery = z.infer<typeof refundsListQuerySchema>;

// ---------------------------------------------------------------------------
// Staff earnings (member-scoped)
// ---------------------------------------------------------------------------

export const staffEarningsSummarySchema = z.object({
  /** Cents. Sum of `staffShareAmount` over the range. */
  totalEarned: z.number().int(),
  /** Cents. Sum of `staffShareAmount` where `staffTransferId` is set (legacy
   * per-checkout split model only). */
  paidOut: z.number().int(),
  /**
   * Cents. Outstanding balance still owed to the member, sourced from the
   * platform ledger (`available + pending` of `getStaffLedgerBalance`), NOT
   * `totalEarned - paidOut`. Under the platform-ledger payout model the payout
   * worker transfers funds at the cycle level and never sets
   * `staffTransferId`, so the ledger — which nets out `PAYOUT_INITIATED`
   * debits — is the authoritative "coming in next payout" figure.
   */
  pendingTransfer: z.number().int(),
  /**
   * Cents. Outstanding cash commission the staff member owes the business
  * (running sum of `LedgerEntry.CASH_STAFF_DEBT` rows, sign-flipped to
  * positive). Settlement rows reverse this balance as online Stripe earnings
  * are applied to the debt. Independent of the date range filter — it's a
  * lifetime outstanding balance.
   */
  cashOwedMinor: z.number().int(),
  /**
   * Cents the member's ledger balance sits below zero, e.g. a refund that
   * took back a share already paid out. `pendingTransfer` floors at 0, so
   * this is the only place the deficit shows; later earnings cover it before
   * the payout worker sends anything. Excludes cash debt (`cashOwedMinor`).
   * Absent from older API payloads.
   */
  balanceShortfallMinor: z.number().int().nonnegative().optional(),
  currency: z.string(),
  /** `false` when the staff member has not connected a Stripe account. */
  connected: z.boolean(),
  /**
   * Estimated next scheduled payout for this member (date + eligible amount),
   * driven by the business's payout schedule. `null` when the member has no
   * connected account or nothing is payout-eligible by the next cycle.
   */
  nextEstimatedPayout: estimatedPayoutSchema.nullable(),
});
export type StaffEarningsSummary = z.infer<typeof staffEarningsSummarySchema>;

export const staffEarningRowSchema = z.object({
  appointmentId: z.string(),
  /** ISO date. */
  paidAt: z.string(),
  customerName: z.string().nullable(),
  serviceLabel: z.string(),
  /** Cents. Service price (excludes tip). */
  price: z.number().int(),
  /** Cents. Tip portion. */
  tip: z.number().int(),
  /** Cents. price + tip. */
  gross: z.number().int(),
  /** Cents. */
  yourShare: z.number().int(),
  /** Cents. Portion that went to the business on this checkout. */
  businessShare: z.number().int(),
  /** Cents. Total Stripe processing fee on the charge. `null` when not enriched yet. */
  fee: z.number().int().nullable(),
  /** Cents. Portion of the Stripe fee deducted from the staff member. `null` when not allocated yet. */
  feeStaffAbsorbed: z.number().int().nullable(),
  /** Cents. Portion of the Stripe fee absorbed by the business. `null` when not allocated yet. */
  feeBusinessAbsorbed: z.number().int().nullable(),
  /**
   * Routing v2 case taken at charge time, when applicable:
   * - `A_1099`: the staff member was the charge destination.
   * - `B_W2_TIP`: standard charge with the tip forwarded to staff.
   * - `C_NONE` / null: no routing (business kept everything, legacy, etc.).
   */
  splitRouting: z.string().nullable(),
  splitStatus: splitStatusSchema,
  splitFailureReason: z.string().nullable(),
  stripeTransferId: z.string().nullable(),
  /**
   * `true` when this appointment's earnings have already been paid out. Under
   * the platform-ledger model payouts happen at the cycle level (no
   * `staffTransferId` is set), so this is derived from the ledger: the
   * earning's `payoutEligibleAt` falls on/before the member's most recent
   * `PAYOUT_INITIATED` sweep. Optional/`false` for legacy per-checkout rows.
   */
  paidOut: z.boolean().optional(),
  /**
   * Cents of `yourShare` that actually reach the member through a platform
   * payout: the STAFF `STAFF_SHARE` / `TIP` ledger rows for this checkout (the
   * deposit and processor rails), or the whole share under the legacy
   * `staffTransferId` model.
   *
   * `0` for cash, which writes only `CASH_*` kinds and is never swept — so
   * `paidOut` stays `false` forever and must not be read as "coming in the
   * next payout". A checkout can be BOTH: an online deposit plus cash on the
   * day funds part of one share, so this is an amount rather than a flag —
   * only this much is upcoming, and the rest is already in the member's hand.
   * Omitted by older API builds; treat that as the whole share.
   */
  payoutBoundMinor: z.number().int().optional(),
});
export type StaffEarningRow = z.infer<typeof staffEarningRowSchema>;

export const staffEarningsListSchema = z.object({
  rows: z.array(staffEarningRowSchema),
});
export type StaffEarningsList = z.infer<typeof staffEarningsListSchema>;

// ---------------------------------------------------------------------------
// Cash deduction transfers (cash-payment commission netted from next payout)
// ---------------------------------------------------------------------------

/**
 * One "cash transfer" row — represents a `CASH_STAFF_DEBT` /
 * `CASH_BUSINESS_OWED` ledger entry pair recorded when a staff member with
 * `cashDeductionEnabled` accepts cash. The business is credited the
 * commission share, and the staff member is debited the same amount;
 * the net is automatically deducted from the staff member's next online
 * Stripe payout cycle. Settlement rows reduce the outstanding balance and
 * convert the collected amount into business-payable platform funds.
 *
 * Sign convention:
 *   • Owner-facing list (`/v1/salons/:id/transfers`): `amountMinor` is
 *     POSITIVE — money owed to the business.
 *   • Staff-facing list (`/v1/staff/me/transfers`): `amountMinor` is
 *     NEGATIVE — money the staff member owes the business (UI shows `-$X`).
 */
export const cashTransferRowSchema = z.object({
  /** LedgerEntry id (unique). */
  id: z.string(),
  /** ISO timestamp the entry was recorded. */
  createdAt: z.string(),
  /** Cents. Signed (see schema doc). */
  amountMinor: z.number().int(),
  currency: z.string(),
  /** Source appointment, when known. */
  appointmentId: z.string().nullable(),
  /** Staff member id. */
  staffId: z.string().nullable(),
  /** Display name for the staff member (owner view). */
  staffName: z.string().nullable(),
  /** Optional ledger note ("Business commission owed by staff…"). */
  notes: z.string().nullable(),
});
export type CashTransferRow = z.infer<typeof cashTransferRowSchema>;

export const cashTransfersListSchema = z.object({
  rows: z.array(cashTransferRowSchema),
  /** Cents. Sum of all `amountMinor` in the response (signed). */
  totalMinor: z.number().int(),
});
export type CashTransfersList = z.infer<typeof cashTransfersListSchema>;

// ---------------------------------------------------------------------------
// Payout breakdown (ledger composition of a single Stripe payout)
// ---------------------------------------------------------------------------

/**
 * One aggregated line of a payout breakdown, grouped by ledger-entry kind.
 * `amountMinor` is signed: positive adds to the payout (earnings/tips),
 * negative reduces it (fees, cash-owe deductions).
 */
export const payoutBreakdownLineSchema = z.object({
  /** Ledger entry kind (e.g. `STAFF_SHARE`) or `STRIPE_BT` in fallback mode. */
  kind: z.string(),
  /** Human-friendly label for the line. */
  label: z.string(),
  /** Signed cents. */
  amountMinor: z.number().int(),
  /** Number of underlying ledger entries / balance transactions aggregated. */
  count: z.number().int(),
});
export type PayoutBreakdownLine = z.infer<typeof payoutBreakdownLineSchema>;

/**
 * Which party kept the tip at checkout time (ledger truth), for Tips Payable
 * digs. Independent of live `TeamMember.tipSplitEnabled` roster config.
 *
 * - `BUSINESS`: tip stayed with the shop (Tips Payable) — attribution known.
 * - `STAFF`: tip left via tip-forward / tip-split — attribution known; do not
 *   credit Tips Payable.
 * - `UNKNOWN`: tip cents exist but destination was not recorded (legacy
 *   `checkout_allocation_v1` notes without `;tip=`, or missing sale tip
 *   component). Do **not** auto-credit Tips Payable from this alone.
 * - `null`: no tip on this entry (`tipMinor === 0`).
 */
export const payoutBreakdownTipPartySchema = z.enum([
  "BUSINESS",
  "STAFF",
  "UNKNOWN",
]);
export type PayoutBreakdownTipParty = z.infer<
  typeof payoutBreakdownTipPartySchema
>;

/** One transaction in the payout: an appointment checkout, or a product sale (`kind: "SALE"`). */
export const payoutBreakdownEntrySchema = z.object({
  id: z.string(),
  /** ISO timestamp. */
  createdAt: z.string(),
  kind: z.string(),
  label: z.string(),
  /**
   * Signed cents before the per-appointment fee below. For a BUSINESS payout,
   * this is service + booking-deposit gross + tips before the separate team
   * commission. For a STAFF payout, this is service share + tips.
   */
  amountMinor: z.number().int(),
  /**
   * Signed cents. Processing + platform fee attributed to this appointment
   * for the account this breakdown is for (negative). `null` when no fee was
   * recorded against the appointment (e.g. cash) or in Stripe fallback mode.
   */
  feeMinor: z.number().int().nullable(),
  /**
   * Cents of tip attributable to this entry from ledger `TIP` rows in the
   * cycle window (0 when none). On BUSINESS cycles this is the BUSINESS TIP
   * credit for the checkout/sale — tip-forward still writes that row and
   * moves the tip to staff via `STAFF_SHARE` / offset, so digs must also read
   * `tipForwarded` / `tipParty` rather than treating every positive tip as
   * Tips Payable. Net of in-window TIP refund mirrors (floored at 0).
   * Defaults keep older API payloads parseable.
   */
  tipMinor: z.number().int().nonnegative().default(0),
  /**
   * `true` only when tip destination is **known** and the tip left to staff.
   * `false` when shop-kept, unknown, or `tipMinor` is 0. Never treat
   * `tipForwarded === false` alone as Tips Payable — require
   * `tipAttributionKnown === true` and `tipParty === "BUSINESS"`.
   * Defaults keep older API payloads parseable.
   */
  tipForwarded: z.boolean().default(false),
  /**
   * Tip party for Tips Payable digs. `null` when `tipMinor` is 0;
   * `BUSINESS` / `STAFF` when destination is known; `UNKNOWN` when tip
   * cents exist but pre-cutover notes omit `;tip=` (or sale tip component is
   * missing). Defaults keep older API payloads parseable.
   */
  tipParty: payoutBreakdownTipPartySchema.nullable().default(null),
  /**
   * `true` when `tipParty` is `BUSINESS` or `STAFF` (or there is no tip).
   * `false` when `tipParty === "UNKNOWN"`. Tips Payable digs must require
   * this flag before crediting shop-kept tips. Defaults to `true` so older
   * payloads without the field remain parseable.
   */
  tipAttributionKnown: z.boolean().default(true),
  appointmentId: z.string().nullable(),
  customerName: z.string().nullable(),
  /**
   * The entry's customer, so the breakdown can open their history. Null for
   * Stripe balance lines, sales without a customer, and the shared walk-in
   * customer. Defaults keep older API payloads parseable.
   */
  customerId: z.string().nullable().default(null),
  serviceLabel: z.string().nullable(),
});
export type PayoutBreakdownEntry = z.infer<typeof payoutBreakdownEntrySchema>;

/**
 * Full breakdown of a single Stripe payout. When the payout can be matched to
 * one or more internal payout cycles, `source` is `"ledger"` and the lines
 * reconcile the gross earnings, fees, and cash-owe deductions down to the net
 * deposited amount. When no internal cycle matches (e.g. legacy / direct
 * charges), `source` is `"stripe"` and the lines are the raw Stripe balance
 * transactions that funded the payout.
 */
export const payoutBreakdownSchema = z.object({
  /** Stripe payout id (`po_…`). */
  payoutId: z.string(),
  currency: z.string(),
  /** Cents. Total amount of the Stripe (bank) payout. */
  payoutAmountMinor: z.number().int(),
  source: z.enum(["ledger", "stripe"]),
  /** Cents. Sum of positive earning lines (service + tips). */
  grossMinor: z.number().int(),
  /** Cents. Sum of processing + platform fee lines (negative). */
  feesMinor: z.number().int(),
  /**
   * Cents. Team commission the business kept out of member service prices
   * (`STAFF_SHARE_OFFSET`), as a negative number. Business breakdowns only;
   * `0` for staff (and Stripe fallback).
   */
  commissionMinor: z.number().int().default(0),
  /**
   * Cents. Cash commission netted out of this payout, as a positive number
   * (shown as a deduction in the UI). `0` when no cash-owe was applied.
   */
  cashOwedDeductedMinor: z.number().int(),
  /**
   * Cents. Cash a member owed the business, settled INTO this payout, as a
   * positive number (an addition). Business breakdowns only; `0` for staff
   * (and Stripe fallback).
   */
  cashCollectedMinor: z.number().int().default(0),
  /** Cents. Net amount paid out (reconciles to `payoutAmountMinor` when matched). */
  netMinor: z.number().int(),
  /** Aggregated lines by kind. */
  lines: z.array(payoutBreakdownLineSchema),
  /** Per-appointment earning entries (the individual transactions). */
  entries: z.array(payoutBreakdownEntrySchema),
});
export type PayoutBreakdown = z.infer<typeof payoutBreakdownSchema>;

// ---------------------------------------------------------------------------
// Retry split (manual retry of a failed staff transfer)
// ---------------------------------------------------------------------------

export const retrySplitResponseSchema = z.object({
  status: splitStatusSchema,
  transferId: z.string().nullable(),
  failureReason: z.string().nullable(),
  staffShare: z.number().int(),
  businessShare: z.number().int(),
});
export type RetrySplitResponse = z.infer<typeof retrySplitResponseSchema>;

// ---------------------------------------------------------------------------
// Manual ledger adjustments (Phase 4)
// ---------------------------------------------------------------------------

/**
 * Record a manual MANUAL_ADJUSTMENT ledger entry pair against a staff member.
 *
 * `amountMinor` is signed against the staff member:
 *   • Positive  →  business credits staff (e.g. missed payout makeup).
 *   • Negative  →  business charges staff (e.g. station rental owed).
 *
 * The matching offset entry is written on the BUSINESS account.
 */
export const createLedgerAdjustmentSchema = z.object({
  staffId: z.string().min(1),
  amountMinor: z
    .number()
    .int()
    .refine((v) => v !== 0, "amountMinor must be non-zero"),
  notes: z.string().min(1).max(500),
  currency: z.string().length(3).optional(),
});
export type CreateLedgerAdjustmentInput = z.infer<
  typeof createLedgerAdjustmentSchema
>;

export const ledgerAdjustmentEntrySchema = z.object({
  id: z.string(),
  accountKind: z.enum(["BUSINESS", "STAFF", "PLATFORM"]),
  amountMinor: z.number().int(),
});
export type LedgerAdjustmentEntry = z.infer<typeof ledgerAdjustmentEntrySchema>;

export const createLedgerAdjustmentResponseSchema = z.object({
  entries: z.array(ledgerAdjustmentEntrySchema),
});
export type CreateLedgerAdjustmentResponse = z.infer<
  typeof createLedgerAdjustmentResponseSchema
>;

export const ledgerAdjustmentRowSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  staffId: z.string().nullable(),
  staffName: z.string().nullable(),
  amountMinor: z.number().int(),
  currency: z.string(),
  notes: z.string().nullable(),
});
export type LedgerAdjustmentRow = z.infer<typeof ledgerAdjustmentRowSchema>;

export const ledgerAdjustmentsListSchema = z.object({
  rows: z.array(ledgerAdjustmentRowSchema),
});
export type LedgerAdjustmentsList = z.infer<typeof ledgerAdjustmentsListSchema>;

// ---------------------------------------------------------------------------
// Ledger activity (business money timeline — "All activity")
// ---------------------------------------------------------------------------

/**
 * Category of one activity timeline row. Checkout-sourced BUSINESS ledger
 * entries (DEPOSIT_REVENUE / SERVICE_REVENUE / TIP / STAFF_SHARE_OFFSET /
 * STRIPE_FEE / PLATFORM_FEE / CASH_RECEIVED) are collapsed into a single
 * `CHECKOUT` row whose `amountMinor` is the business net for that checkout.
 * All other kinds map 1:1 to a row.
 */
export const ledgerActivityCategorySchema = z.enum([
  "CHECKOUT",
  "SALE",
  "CASH_OWED",
  "CASH_SETTLEMENT",
  "PAYOUT",
  "PAYOUT_REVERSAL",
  "REFUND",
  "ADJUSTMENT",
]);
export type LedgerActivityCategory = z.infer<
  typeof ledgerActivityCategorySchema
>;

/** Read-only evidence; candidate IDs never route to operational payment actions. */
export const ledgerCheckoutHistorySchema = z.object({
  checkoutId: z.string(),
  sourceStatus: z.literal("RECONSTRUCTED"),
  sourceVerified: z.literal(false),
  caseId: z.string(),
  amountMinor: z.number().int().nonnegative(),
  currency: z.string(),
  paymentType: z.enum(["CASH", "CARD"]),
  recordedAt: z.string(),
  recordedAtSource: z.enum(["STRIPE", "LEDGER"]),
  serviceAmountMinor: z.number().int().nonnegative().nullable(),
  tipAmountMinor: z.number().int().nonnegative().nullable(),
  candidateAppointmentId: z.string().nullable(),
  candidateCustomerName: z.string().nullable(),
  candidateServiceTitle: z.string().nullable(),
  candidateStartsAt: z.string().nullable(),
  candidateEndsAt: z.string().nullable(),
  timezone: z.string(),
  staffName: z.string().nullable(),
  evidenceNote: z.string(),
  evidenceRef: z.string(),
  ledgerEntryCount: z.number().int().positive(),
  ledgerEvidenceStatus: z.enum(["MATCHED", "CHANGED"]),
  reconstructedAt: z.string(),
  reconstructedCheckout: reconstructedCheckoutRowSchema.optional(),
});
export type LedgerCheckoutHistory = z.infer<typeof ledgerCheckoutHistorySchema>;

export const ledgerActivityRowSchema = z.object({
  /** Stable row id (ledger entry id, or `checkout:<id>` for grouped rows). */
  id: z.string(),
  /** ISO timestamp of the underlying ledger entry (earliest in the group). */
  createdAt: z.string(),
  category: ledgerActivityCategorySchema,
  /**
   * Cents, signed from the business's perspective: positive = money in,
   * negative = money out. For CHECKOUT rows this is the collapsed net
   * (gross − staff share − fees).
   */
  amountMinor: z.number().int(),
  /** Cents. Customer gross (service + tip) for CHECKOUT rows, else null. */
  grossMinor: z.number().int().nullable(),
  /** Cents, positive. Staff cut deducted on CHECKOUT rows, else null. */
  staffShareMinor: z.number().int().nullable(),
  /** Cents, positive. Stripe + platform fees deducted on CHECKOUT rows. */
  feesMinor: z.number().int().nullable(),
  currency: z.string(),
  /** Source appointment, when known (CHECKOUT / CASH_OWED rows). */
  appointmentId: z.string().nullable(),
  /** Customer display name, when known. */
  customerName: z.string().nullable(),
  /** Staff member display name, when relevant (splits / cash / adjustments). */
  staffName: z.string().nullable(),
  /** Flattened service label for CHECKOUT rows. */
  serviceLabel: z.string().nullable(),
  /** Whether the checkout was paid in cash (CHECKOUT rows only). */
  isCash: z.boolean().nullable(),
  /** Ledger note (settlements / adjustments), else null. */
  notes: z.string().nullable(),
  /** Historical evidence only; never an operational checkout/appointment. */
  reconstructedHistory: ledgerCheckoutHistorySchema.optional(),
  /** Source sale for SALE rows (Task 13C); null elsewhere. */
  saleId: z.string().nullable().default(null),
});
export type LedgerActivityRow = z.infer<typeof ledgerActivityRowSchema>;

export const ledgerActivityListSchema = z.object({
  /** One page of timeline rows, newest first. */
  rows: z.array(ledgerActivityRowSchema),
  /** Total number of timeline rows across ALL activity (for pagination). */
  total: z.number().int(),
  /** Cents. Signed sum of `amountMinor` across ALL activity (not the page). */
  totalMinor: z.number().int(),
});
export type LedgerActivityList = z.infer<typeof ledgerActivityListSchema>;

// ---------------------------------------------------------------------------
// Staff ledger (owner view — per-member balances + payout history)
// ---------------------------------------------------------------------------

/**
 * One team member's current ledger position with the business, as seen by the
 * owner. `available` + `pending` mirror {@link payoutsBalanceSchema}: amounts
 * earned on Openings that are awaiting (or settling toward) a Stripe payout to
 * the member's connected account. The signed net fields are a tracked payout
 * position, not a complete accounting of off-platform cash handoffs.
 */
export const staffLedgerMemberSchema = z.object({
  /** TeamMember id. */
  staffId: z.string(),
  /** Display name for the member. */
  name: z.string(),
  /** Cents. Payout-eligible now (settled, next cycle). */
  available: z.number().int(),
  /** Cents. Earned but still in Stripe's settlement window. */
  pending: z.number().int(),
  /** Whether this member's connected Stripe account is enabled for payouts. */
  connected: z.boolean(),
  /**
   * Cents. Outstanding cash commission this member owes the business. This is
   * the positive, lifetime balance of signed `CASH_STAFF_DEBT` entries after
   * payout-cycle settlement reversals.
   */
  cashOwedMinor: z.number().int().nonnegative(),
  /**
   * Cents, signed from the member's perspective:
   * `available + pending - cashOwedMinor`.
   *
   * Positive means the tracked position has the business owing the member;
   * negative means the member owes the business after tracked earnings. This
   * excludes `CASH_STAFF_SHARE` and unrecorded off-platform cash handoffs.
   */
  netBalanceMinor: z.number().int(),
  /**
   * Cents available for the next payout after applying cash debt, floored at
   * zero: `max(0, available - cashOwedMinor)`.
   */
  readyAfterCashMinor: z.number().int().nonnegative(),
  currency: z.string(),
  /**
   * Cents. Lifetime gross customer payment (service + tip) across this
   * member's settled checkouts — i.e. the checkouts that produced ledger
   * activity for the member. Mirrors what the ledger counts so the figure ties
   * out to the underlying checkout rows.
   */
  grossSalesMinor: z.number().int(),
  /**
   * Cents. Lifetime sum of `Payout.amountMinor` `TRANSFERRED` to this member's
   * connected account.
   */
  totalPaidOutMinor: z.number().int(),
  /** Estimated next scheduled payout for this member (date + eligible amount). */
  nextEstimatedPayout: estimatedPayoutSchema.nullable(),
});
export type StaffLedgerMember = z.infer<typeof staffLedgerMemberSchema>;

/** Aggregate gross-sales / paid-out figures across all listed members. */
export const staffLedgerTotalsSchema = z.object({
  /** Cents. Σ `grossSalesMinor` across members. */
  grossSalesMinor: z.number().int(),
  /** Cents. Σ `totalPaidOutMinor` across members. */
  totalPaidOutMinor: z.number().int(),
  /** Cents. Sum of positive tracked member `netBalanceMinor` values. */
  businessOwesStaffMinor: z.number().int().nonnegative(),
  /** Cents. Absolute sum of negative tracked member `netBalanceMinor` values. */
  staffOwesBusinessMinor: z.number().int().nonnegative(),
  currency: z.string(),
});
export type StaffLedgerTotals = z.infer<typeof staffLedgerTotalsSchema>;

export const staffLedgerListSchema = z.object({
  /** Members with split history, sorted by name. */
  members: z.array(staffLedgerMemberSchema),
  /** Aggregate figures for the All-members view. */
  totals: staffLedgerTotalsSchema,
});
export type StaffLedgerList = z.infer<typeof staffLedgerListSchema>;

// ---------------------------------------------------------------------------
// Payout schedule (owner-configurable local payout hour, in the business timezone)
// ---------------------------------------------------------------------------

/**
 * A business's daily payout schedule. The payout worker pays each business
 * once per local day, at the first cron tick at or after `hour` (local
 * wall-clock, 0–23) in `timezone` (IANA, e.g. "America/Los_Angeles");
 * default hour 21 / 9pm. The cron fires hourly, so any timezone + hour
 * combination is honored.
 */
export const payoutScheduleSchema = z.object({
  /** IANA timezone name, e.g. "America/Los_Angeles". */
  timezone: z.string().min(1),
  /** Local wall-clock hour the daily payout runs (0–23). */
  hour: z.number().int().min(0).max(23),
});
export type PayoutSchedule = z.infer<typeof payoutScheduleSchema>;

/**
 * `PATCH /v1/salons/:salonId/payout-schedule` body. Only the hour is
 * editable: `timezone` is the business timezone (`Salon.timezone`), which
 * also sets sales-report and daily close-out days, so the payout schedule
 * never changes it (#943). A `timezone` that is sent must equal the stored
 * one: the dashboard sends the one it showed, so a save made after the
 * business timezone changed is refused instead of landing in another zone.
 */
export const updatePayoutScheduleSchema = z.object({
  /** Local wall-clock hour the daily payout runs (0–23). */
  hour: z.number().int().min(0).max(23),
  /** Optional; must equal the business timezone when sent. */
  timezone: z.string().min(1).optional(),
});
export type UpdatePayoutScheduleInput = z.infer<
  typeof updatePayoutScheduleSchema
>;

/** Mirror of the Prisma `PayoutStatus` enum. */
export const staffPayoutStatusSchema = z.enum([
  "PENDING",
  "TRANSFERRED",
  "FAILED",
  "CANCELLED",
]);
export type StaffPayoutStatus = z.infer<typeof staffPayoutStatusSchema>;

/** One bank-payout cycle from the business to a staff member. */
export const staffPayoutRowSchema = z.object({
  /** Payout id. */
  id: z.string(),
  /** ISO timestamp the payout cycle was created. */
  createdAt: z.string(),
  /** ISO timestamp the cycle was scheduled for. */
  scheduledFor: z.string(),
  /** ISO timestamp the transfer completed, when TRANSFERRED. */
  completedAt: z.string().nullable(),
  /** Cents transferred to the member. */
  amountMinor: z.number().int(),
  currency: z.string(),
  status: staffPayoutStatusSchema,
  /** Failure reason when status is FAILED. */
  failureReason: z.string().nullable(),
  /** Stripe transfer id, when known. */
  stripeTransferId: z.string().nullable(),
});
export type StaffPayoutRow = z.infer<typeof staffPayoutRowSchema>;

export const staffPayoutHistorySchema = z.object({
  /** Payout cycles for the member, newest first. */
  rows: z.array(staffPayoutRowSchema),
});
export type StaffPayoutHistory = z.infer<typeof staffPayoutHistorySchema>;

/**
 * A departing team member's refund shortfall (Sep 2026): how far their payout
 * balance sits below zero. The owner confirms this exact amount; the
 * dashboard moves it to the business in the same transaction that
 * deactivates or removes the member.
 * GET /v1/salons/:salonId/team-members/:memberId/shortfall
 */
export const staffShortfallResponseSchema = z.object({
  shortfallMinor: z.number().int().nonnegative(),
  currency: z.string(),
});
export type StaffShortfallResponse = z.infer<typeof staffShortfallResponseSchema>;
